/**
 * Redirect links that use a retired profile slug to the profile's current slug
 * (EPD-11640). Shared by every slug-keyed layout route so the rule lives in one
 * place: same route, same rest of path, same search and hash — only the slug
 * segment changes.
 */

import { TRPCClientError } from "@trpc/client";

/**
 * Path segments that come before the profile slug. `null` matches any single
 * segment (e.g. the org slug).
 */
export type SlugPathPrefix = readonly (string | null)[];

/** `/o/$orgSlug/p/$profileSlug/...` (creator campaign routes). */
export const ORG_CREATOR_SLUG_PREFIX: SlugPathPrefix = ["o", null, "p"];
/** `/o/$orgSlug/profiles/$profileSlug/...` (profile settings). */
export const ORG_PROFILE_SLUG_PREFIX: SlugPathPrefix = ["o", null, "profiles"];
/** `/portfolio/$profileSlug` (public portfolio, EPD-12500). */
export const PUBLIC_PORTFOLIO_SLUG_PREFIX: SlugPathPrefix = ["portfolio"];

export type SlugLocation = {
  pathname: string;
  /** Including the leading `?`, or empty. */
  searchStr: string;
  /** Excluding the leading `#`, or empty. */
  hash: string;
};

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Build the href for `location` with the profile slug after `prefix` swapped
 * from `fromSlug` to `toSlug`. Returns null when the path doesn't have
 * `fromSlug` at that position, or when nothing would change.
 */
export function buildProfileSlugRedirectHref(
  location: SlugLocation,
  prefix: SlugPathPrefix,
  fromSlug: string,
  toSlug: string,
): string | null {
  if (fromSlug === toSlug) return null;

  const leadingSlash = location.pathname.startsWith("/");
  const segments = (
    leadingSlash ? location.pathname.slice(1) : location.pathname
  ).split("/");

  if (segments.length <= prefix.length) return null;
  const prefixMatches = prefix.every(
    (expected, i) =>
      expected === null || decodeSegment(segments[i]) === expected,
  );
  if (!prefixMatches) return null;
  if (decodeSegment(segments[prefix.length]) !== fromSlug) return null;

  segments[prefix.length] = encodeURIComponent(toSlug);
  const pathname = `/${segments.join("/")}`;
  const hash = location.hash ? `#${location.hash}` : "";
  return `${pathname}${location.searchStr}${hash}`;
}

/**
 * Ask the server for the current slug behind `slug` and return the redirect
 * href when it differs. Any failure (unknown slug, unpublished portfolio,
 * network) returns null so the route keeps its normal not-found behaviour.
 */
export async function findProfileSlugRedirect(options: {
  location: SlugLocation;
  prefix: SlugPathPrefix;
  slug: string;
  resolve: (slug: string) => Promise<{ currentSlug: string }>;
}): Promise<string | null> {
  try {
    const { currentSlug } = await options.resolve(options.slug);
    return buildProfileSlugRedirectHref(
      options.location,
      options.prefix,
      options.slug,
      currentSlug,
    );
  } catch {
    return null;
  }
}

/**
 * Load a public page's data and report whether the server said the slug
 * doesn't exist (a 404). Public routes call the slug resolve only then, so a
 * current slug costs no extra request. Other failures return false and leave
 * the page to show its own error from the cached query.
 */
export async function isPublicPageNotFound<T>(
  load: () => Promise<T>,
): Promise<boolean> {
  try {
    await load();
    return false;
  } catch (error) {
    return error instanceof TRPCClientError && error.data?.httpStatus === 404;
  }
}

/** The parts of the router that following a slug change needs. */
export type SlugChangeRouter = {
  state: { location: SlugLocation };
  navigate(options: { href: string; replace: boolean }): Promise<void>;
  invalidate(): Promise<void>;
};

/**
 * After the current page's profile slug changes, move to the same page under
 * the new slug (keeping the rest of the path, the search and the hash) so the
 * route context reloads with it. Replace, so Back doesn't land on the old
 * slug. When the URL doesn't carry the old slug, just reload the route data.
 */
export function followProfileSlugChange(
  router: SlugChangeRouter,
  prefix: SlugPathPrefix,
  oldSlug: string,
  newSlug: string,
): void {
  const href = buildProfileSlugRedirectHref(
    router.state.location,
    prefix,
    oldSlug,
    newSlug,
  );
  if (href) {
    void router.navigate({ href, replace: true });
  } else {
    void router.invalidate();
  }
}
